import { env } from './env';
import { isLocalHost } from '../lib/isLocalHost';
import type { AppMode } from './mode';

/**
 * Resolved, mode-aware behaviour. Screens read this rather than reaching into
 * env, so the difference between web and kiosk lives in one readable place.
 */
export interface AppConfig {
  mode: AppMode;

  /** Kiosk: the native shell has already granted camera and mic. */
  skipPermissionGate: boolean;
  /** Kiosk: no point warning about Instagram's webview on a signage board. */
  checkInAppBrowser: boolean;
  /** Kiosk: idle anywhere past the attract screen resets the unit. */
  idleTimeoutMs: number | null;
  /**
   * Kiosk: a longer leash on the live camera. The watchdog reads "no touch" as
   * "walked off", but a guest standing in frame cawing is deliberately not
   * touching anything — the normal timeout would reset them mid-attempt.
   */
  captureIdleTimeoutMs: number | null;

  /** Web: fire-and-forget upload after the clip is already on screen. */
  backgroundUpload: boolean;
  /** Kiosk: durable IndexedDB queue with retry. */
  queuedUpload: boolean;
  /** Kiosk: collect a phone number and send by SMS. */
  smsDelivery: boolean;
  /** Web: offer the native share sheet / save. */
  localShare: boolean;

  cameraDeviceId?: string;
  micDeviceId?: string;
  /**
   * Kiosk: never open a camera we cannot name.
   *
   * `facingMode: 'user'` is an *ideal* constraint, and on the kiosk panel no
   * camera reports as "user" — the USB webcam enumerates as EXTERNAL. So the
   * constraint is dropped and Chromium opens the default device, which on this
   * unit may be the RK628 HDMI bridge that kernel-panics the board. When this
   * is set, the fallback is an error instead. See selectKioskCamera.ts.
   */
  requireExplicitCamera: boolean;
  /**
   * The show-night diagnostics view: queue depth, drain, online state.
   *
   * Gated on kiosk MODE rather than on the shell's hostname. An origin
   * allowlist would bake a shell implementation detail into the web app and die
   * silently the day the asset loader's domain changes — and the thing we
   * actually mean is "this is the unit".
   */
  adminEnabled: boolean;

  transitionMode: typeof env.transitionMode;
  /**
   * Whether this device renders the transition media itself.
   *
   * False everywhere since 2026-09-20 — see env.transitionMediaEnabled: the
   * lens carries its own smoke now. The history below is why it was already
   * false on the kiosk before that.
   *
   * False with the remote AR module. The fog was the one heavy GPU job left on
   * the panel once the lens moved to the laptop — a second hardware H.264
   * decode, a 1440x1280 texture upload per frame and a WebGL pass, landing at
   * the exact moment the recording claimed its pre-roll — and the recorded clip
   * froze from that moment on while the picture on screen kept moving. The
   * render machine composites the fog into the stream instead, so it reaches
   * the live view and the clip with no work here. The overlay keeps its timers
   * either way; only the media goes.
   */
  transitionOnPanel: boolean;
  devHud: boolean;
  /**
   * The recording duration readout over the transition and reveal. A testing
   * aid, not something a guest needs — so it is tied to where the page is being
   * served from rather than to the build mode, which would follow a dev build
   * onto a preview URL.
   */
  showRecordingTimer: boolean;

  /**
   * Encoder profile.
   *
   * Worth knowing about the kiosk board even though these are not tuned down for
   * it: the RK3568 has no VP8/VP9 hardware encoder — it ships
   * `OMX.rk.video_decoder.vp8/vp9` but only `OMX.rk.video_encoder.avc` — and
   * MediaRecorder cannot reach that H.264 path (MP4 hangs, and with audio it
   * crashes the renderer). So every recorded frame is libvpx on four A55 cores.
   *
   * These stayed at the web values deliberately. The kiosk's 4fps live preview
   * was blamed on this encoder and it was measured not to be the cause: with the
   * pre-roll disabled outright, and again with the logo compositor disabled, the
   * preview still collapsed to ~5fps. Dropping to 15fps/2.5Mbps changed nothing
   * either. Lowering them only costs the guest picture for no frames back — put
   * them back only against a measurement.
   */
  captureFps: number;
  videoBitsPerSecond: number;
  /**
   * Size of the canvas the recorder captures, or null to record the compositor
   * canvas itself (720x1280). The kiosk records smaller: its software VP8
   * encoder cannot keep up at 720x1280 — see env.kioskRecordHeight.
   */
  recordSize: { width: number; height: number } | null;

  /**
   * Whether a recorder rolls during the LIVE phase to buy the clip its lead-in.
   *
   * On everywhere. It exists as a knob because it was the prime suspect for the
   * kiosk's frame rate, and turning it off is the cleanest way to re-test that:
   * it was not the cause (see above), so the lead-in `preRoll.ts` exists to
   * provide is kept.
   */
  preRollEnabled: boolean;

  /**
   * Time after the camera goes live during which a trigger is ignored, so a
   * guest still walking into frame does not build the clip around the wrong
   * moment. ZERO everywhere since 2026-09-21.
   *
   * It cannot be a flat number, because it does not hold the lens back. The
   * lens transforms on any open mouth it sees, whichever flow it is in, and we
   * have no way to stop it. So any window where we ignore a caw but the lens
   * does not is a window where the two can disagree.
   *
   * The web flow could never afford one. The guest is already holding the phone
   * with their face in frame and usually caws at once, the lens is in this
   * process with no handshake, and rebuilding it under them would cost the very
   * caw they just made. A grace there produced exactly the disagreement above,
   * measured 2026-09-20: smoke and costume on screen, the app still waiting,
   * and the "change me anyway" fallback eventually appearing over a guest who
   * had already transformed.
   *
   * The kiosk could afford one, because it can put the lens back: the guest
   * walks from the touch screen to their mark, and at the end of the grace the
   * panel calls `arm()`, which tells the render machine to hand over a clean
   * face (and, since 2026-09-20, to release a caw made in the meantime rather
   * than drop it). It was dropped anyway, by request, because three seconds of
   * a caw doing nothing reads as a broken kiosk to the guest making it.
   *
   * What that costs, and it is not nothing: `beginSession` on the render
   * machine starts a second lens with the guest in frame and swaps it in on its
   * first frame, which measured 1.3-1.4s on the unit (2026-09-21). That rebuild
   * used to finish inside the grace. Now a guest who caws faster than it
   * transforms on the offer-time lens — the one that opened on an empty room
   * while they walked up — and PixelChefs' smoke does not play from that lens.
   * See stage.html's beginSession. `arm()` still fires, immediately, so a caw
   * made before the panel is listening is still released rather than dropped.
   */
  triggerGraceMs: number;

  /**
   * How long the reveal is held after the transition: the tail of the recording
   * AND the dwell before the result screen, because the recording stops when the
   * hold ends. See `timing.revealHoldMs` for the history and for the floor —
   * under a few hundred ms the fog gets torn down mid-play and does not appear.
   *
   * Per-mode since 2026-09-21, because it is not the same decision twice. The
   * kiosk holds LONGER (5s, asked for in front of the panel): the guest is
   * standing in a room watching themselves in a mirror, and the costume is the
   * whole point of them being there. A phone is held at arm's length by someone
   * who can replay the clip whenever they like, and every extra second is more
   * WebM for them to upload over cellular.
   *
   * It lived in `timing` until a kiosk-only change to it quietly shortened the
   * web reveal as well. That is why it is here.
   */
  revealHoldMs: number;

  /**
   * Cap on the AR module's render rate, or null to leave it uncapped.
   *
   * Camera Kit defaults to uncapped, which on the kiosk GPU starves the rest of
   * the page — see the note at cameraKitModule's createMediaStreamSource call.
   * There is nothing above the camera's own 30fps to win.
   */
  arFpsLimit: number | null;
}

export function buildConfig(mode: AppMode): AppConfig {
  const kiosk = mode === 'kiosk';
  return {
    mode,
    skipPermissionGate: kiosk,
    checkInAppBrowser: !kiosk,
    idleTimeoutMs: kiosk ? env.kioskIdleTimeoutMs : null,
    // Must outlast the trigger fallback, or a struggling guest gets reset to
    // attract before they are ever offered a way through.
    captureIdleTimeoutMs: kiosk ? Math.max(env.kioskIdleTimeoutMs, 90_000) : null,
    backgroundUpload: !kiosk && env.webUploadEnabled && sampled(env.webUploadSampleRate),
    queuedUpload: kiosk,
    smsDelivery: kiosk,
    localShare: !kiosk,
    // Usually blank: on the unit the camera is resolved at RUNTIME from the
    // shell's Camera2 census, because a MediaDevices deviceId is salted per
    // origin and per WebView profile and so cannot be known at build time.
    // This stays as an override for a laptop demo. See selectKioskCamera.ts.
    cameraDeviceId: kiosk ? env.kioskCameraDeviceId || undefined : undefined,
    micDeviceId: kiosk ? env.kioskMicDeviceId || undefined : undefined,
    requireExplicitCamera: kiosk,
    adminEnabled: kiosk || isLocalHost(),
    transitionMode: env.transitionMode,
    // Our fog is off everywhere since 2026-09-20 (the lens has its own smoke);
    // when it is on, the remote path still leaves it to the render machine.
    transitionOnPanel:
      env.transitionMediaEnabled && (env.arModule !== 'remote' || env.transitionOnPanelForced),
    devHud: env.devHud,
    showRecordingTimer: isLocalHost(),
    // Tuned on the unit, not chosen on principle — see the AppConfig comment
    // and env.kioskRecordHeight. Web keeps the original profile; nothing there
    // is encoding in software on four in-order cores.
    captureFps: kiosk ? env.kioskCaptureFps : 30,
    videoBitsPerSecond: kiosk ? env.kioskVideoBitsPerSecond : 4_000_000,
    recordSize: kiosk ? recordSizeFor(env.kioskRecordHeight) : null,
    preRollEnabled: true,
    triggerGraceMs: 0,
    // Literals, not a read of `timing`: index.ts re-exports this file, so
    // importing it back would be circular. 3000 is `timing.revealHoldMs`, which
    // still carries the reasoning and the floor — keep them in step.
    revealHoldMs: kiosk ? 5000 : 3000,
    arFpsLimit: kiosk ? 30 : null,
  };
}

/** 9:16 at the given height, both dimensions even (the encoder wants that). */
function recordSizeFor(height: number): { width: number; height: number } | null {
  if (!Number.isFinite(height) || height <= 0) return null;
  const h = Math.round(height / 2) * 2;
  const w = Math.round((h * 9) / 16 / 2) * 2;
  return { width: w, height: h };
}

/** Decided once per page load, so a guest's session is consistently in or out. */
function sampled(rate: number): boolean {
  if (rate >= 1) return true;
  if (rate <= 0) return false;
  return Math.random() < rate;
}
